import { type Draft } from 'immer';
import { type PartialDeep } from 'type-fest';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElementFrom,
  type CanvasElementId,
  type ColumnId,
  type ElementTargetPath,
  type FormContainerVariantType,
  type ParentStyledElementProps,
  type PropsOf,
} from '@blaze-runtime/engine-domain';

import { type CanvasElementDefinitionInternal } from '../../../canvas-elements/element-definition';
import {
  isElementType,
  isVariantOfFormContainer,
} from '../../../canvas-elements/element-model/element-type-guards';
import { type WizardStep } from '../../../elements/wizard/wizard.models';
import { getWizardSteps } from '../../../elements/wizard/wizard.utils';
import { getPathInSlotAtIndex } from '../../../utils/layout/helpers';
import { placeElementMutably } from '../../../utils/layout/place-element';
import { removeElementMutably } from '../../../utils/layout/remove-element';

import { type QuestionForm } from './wizard-question-forms';

/** What one of a question's Text elements needs to be placed. */
interface StepTextPlacement {
  wizardId: CanvasElementId;
  /** The text the element is born with; its own from then on. */
  seed: string;
  atPath: ElementTargetPath;
  textAreaDefinition: CanvasElementDefinitionInternal;
}

/**
 * Gives every question the Text elements that carry it: the question itself, and an
 * explanation under it when the step asks for one.
 *
 * **Existence is generated from props, never inferred from text.** A question's Text element
 * exists as long as its step does, seeded once from the step's label and owned by the element
 * from then on. Its explanation exists exactly while the step's `explanationEnabled` is true,
 * so clearing the text leaves the element alone and Step settings removes it by clearing that
 * flag instead. Nothing here reads either element's value, which is what stops a regeneration
 * from deleting a text the builder is still typing.
 *
 * No style values are written either: an element is born untouched and the wizard's
 * Question/Explanation typography overlays it at render time through the `parentStyleSource`
 * marker (`childrenOverridesResolver` in `wizard.element.ts`).
 *
 * Runs after each question form's own generation, so the texts land above its field.
 */
export function applyQuestionTextsMutably(
  draft: Draft<EngineStateBase>,
  wizardId: CanvasElementId,
  forms: QuestionForm[],
  textAreaDefinition: CanvasElementDefinitionInternal,
): void {
  const wizard = draft.entities[wizardId];
  if (!isElementType(wizard, 'wizard')) {
    throw new Error('element is not a wizard');
  }
  const steps = getWizardSteps(wizard.props);

  for (const { stepIndex, formId } of forms) {
    const step = steps[stepIndex];
    const form = draft.entities[formId];
    if (!step || !isVariantOfFormContainer(form)) {
      continue;
    }
    applyStepTextsMutably(draft, { wizardId, step, form, textAreaDefinition });
  }
}

/**
 * One question's two Text elements, so that the form reads question, explanation, field.
 *
 * The explanation goes directly after the question, and is placed FIRST when both are missing:
 * the question then lands at the top of the slot and pushes it into second place. That is what
 * `questionIndex + 1` says in both cases at once - a question yet to be placed indexes at -1.
 */
function applyStepTextsMutably(
  draft: Draft<EngineStateBase>,
  {
    wizardId,
    step,
    form,
    textAreaDefinition,
  }: {
    wizardId: CanvasElementId;
    step: WizardStep;
    form: CanvasElementFrom<FormContainerVariantType>;
    textAreaDefinition: CanvasElementDefinitionInternal;
  },
): void {
  const questionElement = liveTextElement(draft, step.questionElementId);
  const explanationElement = liveTextElement(draft, step.explanationElementId);

  if (step.explanationEnabled && !explanationElement) {
    const questionIndex = questionElement ? childrenOf(form).indexOf(questionElement.id) : -1;
    step.explanationElementId = placeStepTextMutably(draft, {
      wizardId,
      seed: '',
      atPath: pathInForm(draft, form, questionIndex + 1),
      textAreaDefinition,
    });
  } else if (!step.explanationEnabled && explanationElement) {
    removeElementMutably(draft, explanationElement.id);
    step.explanationElementId = undefined;
  }

  if (!questionElement) {
    step.questionElementId = placeStepTextMutably(draft, {
      wizardId,
      seed: step.label,
      atPath: pathInForm(draft, form, 0),
      textAreaDefinition,
    });
  }
}

/** Places one of a question's Text elements and reports the id the step must remember. */
function placeStepTextMutably(
  draft: Draft<EngineStateBase>,
  { wizardId, seed, atPath, textAreaDefinition }: StepTextPlacement,
): CanvasElementId {
  // Only generated children carry the marker, so it is not part of any element's own props
  const initProps: PartialDeep<PropsOf<'textArea'>> & ParentStyledElementProps = {
    content: { input: { value: seed } },
    parentStyleSource: { elementId: wizardId },
  };
  const placed = placeElementMutably(draft, {
    elementDefinition: {
      ...textAreaDefinition,
      width: { mode: 'full' },
      height: { mode: 'auto' },
    },
    atPath,
    initProps,
  });
  if (!placed?.elementId) {
    throw new Error('unable to place question text');
  }

  return placed.elementId;
}

/** A placement inside the question form's only slot, where its texts belong above the field. */
function pathInForm(
  draft: Draft<EngineStateBase>,
  form: CanvasElementFrom<FormContainerVariantType>,
  index: number,
): ElementTargetPath {
  return getPathInSlotAtIndex(draft, form.id, formSlotIdOf(form), index);
}

/** What the question form holds, in order. */
function childrenOf(form: CanvasElementFrom<FormContainerVariantType>): CanvasElementId[] {
  return form.slots[formSlotIdOf(form)]?.children ?? [];
}

function formSlotIdOf(form: CanvasElementFrom<FormContainerVariantType>): ColumnId {
  const slotId = form.slots.ids[0];
  if (!slotId) {
    throw new Error('question form has no slot');
  }

  return slotId;
}

/**
 * The Text element a step's persisted id points at, when it still exists. A dangling id reads
 * as "no element", so the declarative pass simply creates the one the props ask for.
 */
function liveTextElement(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId | undefined,
): CanvasElementFrom<'textArea'> | undefined {
  const element = elementId ? draft.entities[elementId] : undefined;

  return isElementType(element, 'textArea') ? element : undefined;
}
